/**
 * Wishlist Add All carousel — Quick Buy modal cards that flip through the queue.
 */
import { getLabels, getConfig } from '../config.js';
import { fetchProductOptions } from '../api/product-options.js';
import { addSimpleProduct, addVariation } from '../api/cart.js';
import { refreshCartFragments } from '../integrations/cart-fragments.js';
import { customerMessage } from '../utils/errors.js';
import { el, empty, qs } from '../utils/dom.js';
import { visibleVariations, stockStatusNode } from '../modal/renderer.js';
import { refreshConvertedPrices } from '../integrations/kestrel-currency.js';

function ensureModal() {
	let root = document.getElementById('wcqb-modal');
	if (!root) {
		root = document.createElement('div');
		root.id = 'wcqb-modal';
		root.className = 'wcqb-modal';
		root.hidden = true;
		root.setAttribute('aria-hidden', 'true');
		root.innerHTML = `
			<div class="wcqb-modal__backdrop" data-wcqb-close="backdrop"></div>
			<div class="wcqb-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="wcqb-modal-title" tabindex="-1">
				<button type="button" class="wcqb-modal__close" data-wcqb-close="button" aria-label="Close"><span aria-hidden="true">&times;</span></button>
				<div class="wcqb-modal__body" data-wcqb-body>
					<div class="wcqb-modal__content" data-wcqb-content></div>
				</div>
				<div class="wcqb-modal__live" data-wcqb-live aria-live="polite" aria-atomic="true"></div>
			</div>
		`;
		document.body.appendChild(root);
	}
	return root;
}

function lockScroll(lock) {
	document.documentElement.classList.toggle('wcqb-modal-open', lock);
	document.body.classList.toggle('wcqb-modal-open', lock);
}

function priceNode(html, className) {
	const wrap = el('div', { className });
	if (html) {
		wrap.innerHTML = html;
	}
	return wrap;
}

export function createWishlistCarousel() {
	const state = {
		queue: [],
		index: 0,
		adding: false,
		options: null,
		selectedVariationId: null,
		trigger: null,
		form: null,
	};

	let root = null;
	let dialog = null;
	let content = null;
	let live = null;
	let stage = null;

	function bindShell() {
		root = ensureModal();
		dialog = qs('.wcqb-modal__dialog', root);
		content = qs('[data-wcqb-content]', root);
		live = qs('[data-wcqb-live]', root);
		content.hidden = false;

		// PHP modal shell includes a static loading row; hide it so only carousel content shows.
		const loadingPlaceholder = qs('[data-wcqb-loading]', root);
		if (loadingPlaceholder) {
			loadingPlaceholder.hidden = true;
		}
	}

	function open(queue, trigger, form) {
		bindShell();
		state.queue = Array.isArray(queue) ? queue.slice() : [];
		state.index = 0;
		state.adding = false;
		state.options = null;
		state.selectedVariationId = null;
		state.trigger = trigger || null;
		state.form = form || null;

		if (!state.queue.length) {
			return;
		}

		root.hidden = false;
		root.setAttribute('aria-hidden', 'false');
		root.classList.add('is-open', 'wcqb-wishlist-carousel-open');
		lockScroll(true);
		showCurrent(false);
		if (dialog) {
			dialog.focus();
		}
	}

	function close() {
		if (state.adding) {
			return;
		}
		bindShell();
		root.classList.remove('is-open', 'wcqb-wishlist-carousel-open');
		root.hidden = true;
		root.setAttribute('aria-hidden', 'true');
		lockScroll(false);
		if (live) {
			live.textContent = '';
		}
		if (state.trigger && typeof state.trigger.focus === 'function') {
			state.trigger.focus();
		}
		state.queue = [];
		state.index = 0;
	}

	function currentItem() {
		return state.queue[state.index] || null;
	}

	async function showCurrent(animate) {
		const item = currentItem();
		if (!item) {
			finish();
			return;
		}

		bindShell();
		empty(content);

		stage = el('div', {
			className: 'wcqb-wishlist-carousel' + (animate ? ' is-enter' : ''),
		});

		const labels = getLabels();
		stage.appendChild(
			buildHeader(item, null, {
				className: item.stockClass || (item.inStock !== false && item.purchasable ? 'in-stock' : 'out-of-stock'),
				text:
					item.stockText ||
					(item.inStock !== false && item.purchasable
						? labels.inStock || 'In stock'
						: labels.outOfStock || 'Out of stock'),
				inStock: !!(item.inStock !== false && item.purchasable),
			})
		);

		const body = el('div', { className: 'wcqb-wishlist-carousel__body' });
		stage.appendChild(body);
		content.appendChild(stage);

		if (!item.purchasable && item.type !== 'variable') {
			body.appendChild(unavailableActions(item));
			refreshConvertedPrices();
			return;
		}

		if (item.type === 'simple') {
			renderSimple(body, item);
			refreshConvertedPrices();
			return;
		}

		body.appendChild(
			el('div', { className: 'wcqb-modal__loading is-visible' }, [
				el('span', { className: 'wcqb-modal__spinner', 'aria-hidden': 'true' }),
				el('span', { text: labels.loading || 'Loading pack options…' }),
			])
		);

		try {
			const options = await fetchProductOptions(item.productId, 'wishlist');
			if (currentItem() !== item) {
				return;
			}
			state.options = options;
			refreshHeader(item, options);
			empty(body);
			renderVariable(body, item, options);
			refreshConvertedPrices();
		} catch (error) {
			if (currentItem() !== item) {
				return;
			}
			empty(body);
			body.appendChild(
				el('p', {
					className: 'wcqb-modal__message',
					text: customerMessage(error),
				})
			);
			body.appendChild(unavailableActions(item));
			refreshConvertedPrices();
		}
	}

	function buildHeader(item, options, stockStatus) {
		const header = el('div', { className: 'wcqb-modal__header' });
		const image = (options && options.image) || item.image;
		if (image && image.src) {
			header.appendChild(
				el('img', {
					className: 'wcqb-modal__image',
					src: image.src,
					alt: image.alt || item.title || '',
					width: '96',
					height: '96',
				})
			);
		}

		const title = (options && options.title) || item.title || '';
		const priceHtml = (options && options.price_html) || item.priceHtml || '';
		const textChildren = [
			el('h2', {
				className: 'wcqb-modal__title',
				id: 'wcqb-modal-title',
				text: title,
			}),
		];

		if (priceHtml) {
			textChildren.push(priceNode(priceHtml, 'wcqb-modal__from-price price'));
		}

		if (stockStatus) {
			textChildren.push(stockStatusNode(stockStatus));
		}

		header.appendChild(el('div', { className: 'wcqb-modal__heading' }, textChildren));
		return header;
	}

	function stockForItem(item, options) {
		const labels = getLabels();
		if (options) {
			const available = (options.variations || []).some((v) => v.purchasable);
			if (options.mode === 'unavailable' || !available) {
				return {
					className: 'out-of-stock',
					text: labels.outOfStock || 'Out of stock',
				};
			}
			return {
				className: 'in-stock',
				text: labels.inStock || 'In stock',
			};
		}
		return {
			className: item.stockClass || (item.inStock ? 'in-stock' : 'out-of-stock'),
			text:
				item.stockText ||
				(item.inStock ? labels.inStock || 'In stock' : labels.outOfStock || 'Out of stock'),
			inStock: !!item.inStock,
		};
	}

	function refreshHeader(item, options) {
		if (!stage) {
			return;
		}
		const old = qs('.wcqb-modal__header', stage);
		const next = buildHeader(item, options, stockForItem(item, options));
		if (old && old.parentNode) {
			old.parentNode.replaceChild(next, old);
		}
	}

	function renderSimple(body, item) {
		const labels = getLabels();
		body.appendChild(
			el('div', { className: 'wcqb-modal__actions wcqb-wishlist-carousel__actions' }, [
				el('button', {
					type: 'button',
					className: 'wcqb-modal__add',
					'data-action': 'wishlist-add',
					text: labels.addToBasket || 'Add to basket',
				}),
				el('button', {
					type: 'button',
					className: 'wcqb-wishlist-carousel__skip',
					'data-action': 'wishlist-skip',
					text: labels.skip || 'Skip',
				}),
			])
		);
	}

	function renderVariable(body, item, options) {
		const labels = getLabels();
		const config = getConfig();
		const variations = visibleVariations(options.variations || []);
		const unavailable =
			options.mode === 'unavailable' ||
			(!(options.variations || []).some((v) => v.purchasable) && options.mode !== 'unsupported');

		if (unavailable || !variations.length) {
			if (options.mode === 'unsupported') {
				body.appendChild(
					el('p', {
						className: 'wcqb-modal__message',
						text:
							(options.messages && options.messages.instruction) ||
							labels.unsupported ||
							'Please choose options on the product page.',
					})
				);
			}
			body.appendChild(unavailableActions(item, options));
			return;
		}

		let selectedId = null;
		// Prefer an in-session choice so the user can change option.
		if (state.selectedVariationId) {
			const current = variations.find(
				(v) => Number(v.variation_id) === Number(state.selectedVariationId) && v.purchasable
			);
			if (current) {
				selectedId = Number(current.variation_id);
			}
		}
		if (!selectedId && item.variationId) {
			const saved = variations.find(
				(v) => Number(v.variation_id) === Number(item.variationId) && v.purchasable
			);
			if (saved) {
				selectedId = Number(saved.variation_id);
			}
		}
		if (!selectedId && options.default_variation_id) {
			const def = variations.find(
				(v) => Number(v.variation_id) === Number(options.default_variation_id) && v.purchasable
			);
			if (def) {
				selectedId = Number(def.variation_id);
			}
		}
		state.selectedVariationId = selectedId;

		body.appendChild(
			el('p', {
				className: 'wcqb-modal__instruction',
				text: (options.messages && options.messages.instruction) || labels.chooseOption,
			})
		);

		const packs = el('div', {
			className: 'wcqb-modal__packs',
			role: 'group',
		});
		variations.forEach((variation) => {
			if (!variation.purchasable) {
				return;
			}
			const selected = Number(variation.variation_id) === Number(state.selectedVariationId);
			const classes = ['wcqb-modal__pack'];
			if (selected) {
				classes.push('is-selected');
			}
			if (variation.sale_price_html) {
				classes.push('is-sale');
			}
			const children = [
				el('span', { className: 'wcqb-modal__pack-label', text: variation.label || '' }),
			];
			if (config.showPriceInOption !== false && variation.price_html) {
				children.push(priceNode(variation.price_html, 'wcqb-modal__pack-price'));
			}
			packs.appendChild(
				el('div', { className: 'wcqb-modal__pack-row' }, [
					el(
						'button',
						{
							type: 'button',
							className: classes.join(' '),
							'data-variation-id': String(variation.variation_id),
							'data-action': 'select-pack',
							'aria-pressed': selected ? 'true' : 'false',
						},
						children
					),
				])
			);
		});
		body.appendChild(packs);

		const selected = variations.find(
			(v) => Number(v.variation_id) === Number(state.selectedVariationId)
		);
		const priceArea = el('div', { className: 'wcqb-modal__price-area' });
		if (selected) {
			priceArea.appendChild(priceNode(selected.price_html || '', 'wcqb-modal__price-now'));
		} else {
			priceArea.appendChild(
				el('p', {
					className: 'wcqb-modal__price-placeholder',
					text: labels.chooseOption || 'Choose an option',
				})
			);
		}
		body.appendChild(priceArea);

		body.appendChild(
			el('div', { className: 'wcqb-modal__actions wcqb-wishlist-carousel__actions' }, [
				el('button', {
					type: 'button',
					className: 'wcqb-modal__add',
					'data-action': 'wishlist-add',
					disabled: selected ? null : '',
					text: labels.addToBasket || 'Add to basket',
				}),
				el('button', {
					type: 'button',
					className: 'wcqb-wishlist-carousel__skip',
					'data-action': 'wishlist-skip',
					text: labels.skip || 'Skip',
				}),
			])
		);
	}

	/**
	 * Out-of-stock / unsupported: More Info + Skip, 50/50 like the archive modal.
	 */
	function unavailableActions(item, options) {
		const labels = getLabels();
		const permalink = (options && options.permalink) || item.permalink || '#';
		return el('div', { className: 'wcqb-modal__actions wcqb-wishlist-carousel__actions' }, [
			el('a', {
				className: 'wcqb-modal__view-full',
				href: permalink,
				text: labels.viewFull || 'More Info',
				'data-action': 'view-full',
			}),
			el('button', {
				type: 'button',
				className: 'wcqb-wishlist-carousel__skip wcqb-wishlist-carousel__skip--primary',
				'data-action': 'wishlist-skip',
				text: labels.skip || 'Skip',
			}),
		]);
	}

	function markRowAdded(tiItemId) {
		if (!state.form || !tiItemId) {
			return;
		}
		const row = state.form.querySelector(`tr[data-wcqb-ti-item="${tiItemId}"]`);
		if (row) {
			row.classList.add('wcqb-wishlist-item--added');
		}
	}

	async function addCurrent() {
		const item = currentItem();
		const labels = getLabels();
		if (!item || state.adding) {
			return;
		}

		state.adding = true;
		const addBtn = content && content.querySelector('[data-action="wishlist-add"]');
		if (addBtn) {
			addBtn.disabled = true;
			addBtn.textContent = labels.adding || 'Adding…';
		}

		try {
			let cart;
			if (item.type === 'simple') {
				cart = await addSimpleProduct(item.productId, 1);
			} else {
				const variation = (state.options && state.options.variations || []).find(
					(v) => Number(v.variation_id) === Number(state.selectedVariationId) && v.purchasable
				);
				if (!variation) {
					throw Object.assign(new Error(labels.chooseRequired || 'Please choose an option.'), {
						code: 'choose_pack',
					});
				}
				cart = await addVariation(variation.variation_id, variation.attributes, 1);
			}

			if (addBtn) {
				addBtn.setAttribute('data-product_id', String(item.productId || ''));
				addBtn.setAttribute('data-product_name', item.title || '');
				addBtn.setAttribute('data-quantity', '1');
			}
			refreshCartFragments(cart, addBtn);
			markRowAdded(item.tiItemId);
			if (live) {
				live.textContent = '';
			}
			state.adding = false;
			await advance(true);
		} catch (error) {
			state.adding = false;
			if (addBtn) {
				addBtn.disabled = false;
				addBtn.textContent = labels.addToBasket || 'Add to basket';
			}
			if (live) {
				live.textContent = customerMessage(error);
			}
		}
	}

	function advance(animate) {
		return new Promise((resolve) => {
			const next = state.index + 1;
			if (next >= state.queue.length) {
				finish();
				resolve();
				return;
			}

			const reduce =
				window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			if (stage && animate && !reduce) {
				stage.classList.remove('is-enter');
				stage.classList.add('is-exit');
				window.setTimeout(() => {
					state.index = next;
					state.options = null;
					state.selectedVariationId = null;
					showCurrent(true).then(resolve);
				}, 280);
			} else {
				state.index = next;
				state.options = null;
				state.selectedVariationId = null;
				showCurrent(Boolean(animate)).then(resolve);
			}
		});
	}

	function finish() {
		// No "All done" card — just close when the queue is finished.
		state.adding = false;
		close();
	}

	function onClick(event) {
		if (!root || root.hidden || !root.classList.contains('wcqb-wishlist-carousel-open')) {
			return;
		}

		const closeEl = event.target.closest('[data-wcqb-close]');
		if (closeEl) {
			const type = closeEl.getAttribute('data-wcqb-close');
			if (type === 'backdrop' && getConfig().closeOnBackdrop === false) {
				return;
			}
			event.preventDefault();
			close();
			return;
		}

		const pack = event.target.closest('[data-action="select-pack"]');
		if (pack && content.contains(pack)) {
			event.preventDefault();
			event.stopImmediatePropagation();
			state.selectedVariationId = Number(pack.getAttribute('data-variation-id'));
			const item = currentItem();
			if (item && state.options) {
				const body = qs('.wcqb-wishlist-carousel__body', content);
				if (body) {
					empty(body);
					renderVariable(body, item, state.options);
					refreshConvertedPrices();
				}
			}
			return;
		}

		const add = event.target.closest('[data-action="wishlist-add"]');
		if (add && content.contains(add)) {
			event.preventDefault();
			addCurrent();
			return;
		}

		const skip = event.target.closest('[data-action="wishlist-skip"]');
		if (skip && content.contains(skip)) {
			event.preventDefault();
			if (!state.adding) {
				advance(true);
			}
			return;
		}

		const doneClose = event.target.closest('[data-action="wishlist-close"]');
		if (doneClose && content.contains(doneClose)) {
			event.preventDefault();
			state.adding = false;
			close();
		}
	}

	function onKeydown(event) {
		if (!root || root.hidden || !root.classList.contains('wcqb-wishlist-carousel-open')) {
			return;
		}
		if (event.key === 'Escape' && !state.adding) {
			close();
		}
	}

	document.addEventListener('click', onClick);
	document.addEventListener('keydown', onKeydown);

	return { open, close };
}

function wishlistRoot(fromNode) {
	if (fromNode && fromNode.closest) {
		const root = fromNode.closest('[data-wcqb-wishlist]');
		if (root) {
			return root;
		}
	}
	return document.querySelector('[data-wcqb-wishlist]');
}

function readQueue(root) {
	if (!root) {
		return [];
	}
	const script = root.querySelector('script.wcqb-wishlist-queue-data');
	if (script && script.textContent) {
		try {
			const parsed = JSON.parse(script.textContent);
			return Array.isArray(parsed) ? parsed : [];
		} catch (e) {
			// Fall through.
		}
	}
	try {
		const form = root.querySelector('form.wcqb-wishlist');
		const parsed = JSON.parse((form && form.getAttribute('data-wcqb-wishlist-queue')) || '[]');
		return Array.isArray(parsed) ? parsed : [];
	} catch (e) {
		return [];
	}
}

function writeQueue(root, queue) {
	if (!root) {
		return;
	}
	const script = root.querySelector('script.wcqb-wishlist-queue-data');
	if (script) {
		script.textContent = JSON.stringify(queue);
	}
}

function removeFromQueue(root, tiItemId) {
	const queue = readQueue(root).filter((item) => Number(item.tiItemId) !== Number(tiItemId));
	writeQueue(root, queue);
}

/**
 * Own the remove button entirely.
 *
 * TI's success handler shows a modal AND replaces
 * `div.tinv-wishlist.woocommerce.tinv-wishlist-clear` with its own HTML —
 * which would wipe our [wc_quick_buy_wishlist] table. We stop TI, run the same AJAX,
 * then quietly drop the row.
 */
function bindTiRemoveSync() {
	const $ = window.jQuery;
	if (!$) {
		return;
	}

	const removing = new WeakSet();

	const isActivateKey = (event) => {
		if (event.type !== 'keydown') {
			return true;
		}
		const key = event.key !== undefined ? event.key : event.keyCode;
		return (
			key === 'Enter' ||
			key === 13 ||
			key === ' ' ||
			key === 'Spacebar' ||
			key === 32
		);
	};

	const runRemove = (btn) => {
		if (!btn || removing.has(btn)) {
			return;
		}
		const row = btn.closest('tr.wishlist_item');
		const form = btn.closest('form.wcqb-wishlist');
		const ti = window.tinvwl_add_to_wishlist;
		if (!row || !form || !ti || !ti.wc_ajax_url) {
			return;
		}

		removing.add(btn);
		btn.disabled = true;
		row.classList.add('wcqb-wishlist-item--removing');

		const data = {
			'tinvwl-product_id': btn.value,
			'tinvwl-action': 'remove',
			'tinvwl-security': ti.nonce,
			'tinvwl-paged': form.getAttribute('data-tinvwl_paged') || '1',
			'tinvwl-per-page': form.getAttribute('data-tinvwl_per_page') || '100',
			'tinvwl-sharekey': form.getAttribute('data-tinvwl_sharekey') || '',
		};
		if (ti.wpml) {
			data.lang = ti.wpml;
		}
		if (ti.wpml_default) {
			data.lang_default = ti.wpml_default;
		}
		if (ti.stats) {
			data.stats = ti.stats;
		}

		$.ajax({
			url: ti.wc_ajax_url,
			method: 'POST',
			cache: false,
			data,
			beforeSend(xhr) {
				xhr.setRequestHeader('X-WP-Nonce', ti.nonce);
			},
		})
			.done((response) => {
				stripTiModals();
				if (!response || !response.status) {
					row.classList.remove('wcqb-wishlist-item--removing');
					btn.disabled = false;
					removing.delete(btn);
					return;
				}

				const root = wishlistRoot(row);
				const tiItemId = row.getAttribute('data-wcqb-ti-item') || btn.value;
				row.remove();
				if (root && tiItemId) {
					removeFromQueue(root, tiItemId);
				}

				// Keep TI's client-side wishlist data in sync when possible.
				if (response.wishlists_data && typeof jQuery.fn.tinvwl_get_wishlist_data === 'function') {
					try {
						jQuery.fn.tinvwl_get_wishlist_data();
					} catch (e) {
						// ignore
					}
				}

				stripTiModals();
			})
			.fail(() => {
				row.classList.remove('wcqb-wishlist-item--removing');
				btn.disabled = false;
				removing.delete(btn);
			});
	};

	const onRemoveIntent = (event) => {
		const btn = event.target.closest && event.target.closest('form.wcqb-wishlist button[name="tinvwl-remove"]');
		if (!btn) {
			return;
		}
		if (!isActivateKey(event)) {
			return;
		}
		event.preventDefault();
		event.stopImmediatePropagation();
		runRemove(btn);
	};

	// Capture phase so TI's body-delegated handler never runs.
	document.addEventListener('click', onRemoveIntent, true);
	document.addEventListener('keydown', onRemoveIntent, true);
}

function stripTiModals() {
	document.querySelectorAll('body > .tinv-wishlist .tinv-modal').forEach((node) => {
		node.remove();
	});
	document.querySelectorAll('.tinv-modal.tinv-modal-open, .tinv-wishlist .tinv-modal').forEach((node) => {
		if (node.closest && node.closest('[data-wcqb-wishlist]')) {
			return;
		}
		node.remove();
	});
}

function suppressTiCartModals() {
	const $ = window.jQuery;
	if (!$) {
		return;
	}

	// If TI still emits cart AJAX on our page, drop its success dialog.
	$('body').on('tinvwl_wishlist_ajax_response', (event, el) => {
		if (!document.querySelector('[data-wcqb-wishlist]')) {
			return;
		}
		const name = el && el.getAttribute ? String(el.getAttribute('name') || '') : '';
		const isCart =
			name.indexOf('tinvwl-add-to-cart') !== -1 ||
			name.indexOf('product_all') !== -1 ||
			name.indexOf('product_selected') !== -1;
		if (!isCart) {
			return;
		}
		window.setTimeout(stripTiModals, 0);
		window.setTimeout(stripTiModals, 80);
	});
}

/**
 * Block TI's native Add All / Buy Now handlers inside our wishlist root.
 */
function blockTiWishlistCartHandlers() {
	document.addEventListener(
		'click',
		(event) => {
			const root = event.target.closest('[data-wcqb-wishlist]');
			if (!root) {
				return;
			}

			const tiCart = event.target.closest(
				'button[name="tinvwl-add-to-cart"], button[name="tinvwl-action-product_all"]'
			);
			if (tiCart) {
				event.preventDefault();
				event.stopImmediatePropagation();
			}
		},
		true
	);
}

export function initWishlist() {
	const carousel = createWishlistCarousel();
	bindTiRemoveSync();
	suppressTiCartModals();
	blockTiWishlistCartHandlers();

	document.addEventListener('wcQuickBuy:addSucceeded', (event) => {
		const detail = event.detail || {};
		if (detail.context !== 'wishlist' || !detail.productId) {
			return;
		}
		const row = document.querySelector(
			`[data-wcqb-wishlist] tr.wishlist_item[data-wcqb-product-id="${detail.productId}"]`
		);
		if (row) {
			row.classList.add('wcqb-wishlist-item--added');
		}
	});

	// Capture phase so we win over TI's delegated jQuery handlers.
	document.addEventListener(
		'click',
		(event) => {
			const button = event.target.closest('[data-wcqb-wishlist-add-all]');
			if (!button) {
				return;
			}
			event.preventDefault();
			event.stopImmediatePropagation();
			stripTiModals();

			const root = wishlistRoot(button);
			const form = root ? root.querySelector('form.wcqb-wishlist') : button.closest('form.wcqb-wishlist');
			const queue = readQueue(root);
			if (!queue.length) {
				return;
			}
			carousel.open(queue, button, form);
		},
		true
	);
}
